import { useState, type FormEvent } from 'react'
import { useMe } from '../hooks/useMe'
import { useEvent } from '../hooks/useEvents'
import { useCatering, useCreateCatering, usePatchCatering, useDeleteCatering } from '../hooks/useCatering'
import { formatGBP, parseGBP } from '../lib/money'
import { downloadCsv, dateStamp, fileSlug } from '../lib/download'
import {
  ModalShell, Field, inputClass, CancelButton, PrimaryButton, DangerButton, ServerError
} from '../components/modal-form'
import { Icon } from '../components/Icon'
import { useArchived } from '../contexts/ArchiveContext'
import { CATERING_STATUSES } from '../types'
import type { EventCatering, EventCateringBody, CateringStatus } from '../types'

// CateringTab (Feature 3) — per-event catering requirements: offer, supplier,
// cost (or a "to be confirmed" note), status and whether it's an upsell.
// Exports to CSV; a global all-events catering PDF lives in Reports.

interface Props { eventId: number }

const STATUS_STYLE: Record<CateringStatus, { bg: string; fg: string; label: string }> = {
  confirmed: { bg: '#dcebe5', fg: '#0a4a3a', label: 'Confirmed' },
  tbc: { bg: '#f5edd5', fg: '#9b7f2a', label: 'To confirm' },
  proposed: { bg: '#eee', fg: '#57534e', label: 'Proposed' }
}

function costText(c: EventCatering): string {
  if (c.costPence != null) return formatGBP(c.costPence)
  return c.costNote ?? '—'
}

function retailText(c: EventCatering): string {
  return c.retailPricePence != null ? formatGBP(c.retailPricePence) : '—'
}

export function CateringTab({ eventId }: Props) {
  const archived = useArchived()
  const { data: me } = useMe()
  const canFinancial = !!me?.hasFinancial
  const { data: event } = useEvent(eventId)
  const { data: rows, isLoading, error } = useCatering(eventId)
  const [editing, setEditing] = useState<EventCatering | 'new' | null>(null)

  function handleExport() {
    const header = ['Offer', 'Supplier', 'Cost', 'Retail price', 'Status', 'Upsell', 'Notes']
    const body = (rows ?? []).map((c) => [
      c.offer, c.supplier ?? '', costText(c), retailText(c), STATUS_STYLE[c.status].label, c.isUpsell ? 'Yes' : 'No', c.notes ?? ''
    ])
    downloadCsv(`nymr-catering-${fileSlug(event?.name ?? 'event')}-${dateStamp()}.csv`, [header, ...body])
  }

  if (isLoading) return <div className="text-ink-soft text-sm font-mono">Loading catering…</div>
  if (error) {
    return (
      <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
        <div className="font-semibold mb-1">Could not load catering</div>
        <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
      </div>
    )
  }

  const list = rows ?? []

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-xl">Catering</h2>
          <p className="text-sm text-ink-soft max-w-2xl">
            The catering offers for this event — included items and upsells, suppliers
            and costs. A global catering report across all events lives in Reports.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleExport}
            disabled={list.length === 0}
            className="px-3 py-1.5 text-sm font-semibold rounded border border-line text-ink-soft hover:text-green hover:border-green transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <Icon name="download" size={14} /> Export CSV
          </button>
          {!archived && (
            <button
              type="button"
              onClick={() => setEditing('new')}
              className="px-3 py-1.5 text-sm font-semibold rounded bg-green text-parchment hover:bg-green/90"
            >
              + Add catering
            </button>
          )}
        </div>
      </div>

      {list.length === 0 ? (
        <div className="bg-white border border-line rounded p-10 text-center">
          <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ background: '#f5edd5', color: '#9b7f2a' }}>
            <Icon name="utensils" size={22} />
          </div>
          <h3 className="font-display text-lg mb-1">No catering recorded</h3>
          <p className="text-sm text-ink-soft max-w-md mx-auto">Add the catering offers for this event — what's included, upsells, suppliers and costs.</p>
        </div>
      ) : (
        <div className="bg-white border border-line rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-ink-soft">
                <th className="px-3 py-2 font-semibold">Offer</th>
                <th className="px-3 py-2 font-semibold">Supplier</th>
                <th className="px-3 py-2 font-semibold">Cost</th>
                {canFinancial && <th className="px-3 py-2 font-semibold">Retail</th>}
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {list.map((c) => (
                <tr key={c.id} className={`hover:bg-parchment/30 ${archived ? '' : 'cursor-pointer'}`} onClick={() => { if (!archived) setEditing(c) }}>
                  <td className="px-3 py-2">
                    <div className="font-semibold flex items-center gap-2">
                      {c.offer}
                      {c.isUpsell && <span className="text-[11px] font-mono px-1.5 py-0.5 rounded border border-line text-ink-soft">upsell</span>}
                    </div>
                    {c.notes && <div className="text-xs text-ink-soft mt-0.5">{c.notes}</div>}
                  </td>
                  <td className="px-3 py-2">{c.supplier ?? '—'}</td>
                  <td className="px-3 py-2 font-mono">{costText(c)}</td>
                  {canFinancial && <td className="px-3 py-2 font-mono">{retailText(c)}</td>}
                  <td className="px-3 py-2">
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded" style={{ background: STATUS_STYLE[c.status].bg, color: STATUS_STYLE[c.status].fg }}>
                      {STATUS_STYLE[c.status].label}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {!archived && <Icon name="edit" size={14} className="text-ink-soft" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <CateringEditor
          eventId={eventId}
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}

function CateringEditor({ eventId, initial, onClose }: { eventId: number; initial: EventCatering | null; onClose: () => void }) {
  const isEditing = initial != null
  const { data: me } = useMe()
  const canFinancial = !!me?.hasFinancial
  const createMut = useCreateCatering(eventId)
  const patchMut = usePatchCatering(eventId)
  const deleteMut = useDeleteCatering(eventId)

  const [offer, setOffer] = useState(initial?.offer ?? '')
  const [supplier, setSupplier] = useState(initial?.supplier ?? '')
  const [cost, setCost] = useState(initial?.costPence == null ? '' : formatGBP(initial.costPence))
  const [retailPrice, setRetailPrice] = useState(initial?.retailPricePence == null ? '' : formatGBP(initial.retailPricePence))
  const [costNote, setCostNote] = useState(initial?.costNote ?? '')
  const [status, setStatus] = useState<CateringStatus>(initial?.status ?? 'tbc')
  const [isUpsell, setIsUpsell] = useState(initial?.isUpsell ?? false)
  const [notes, setNotes] = useState(initial?.notes ?? '')

  const offerError = offer.trim().length === 0 ? 'Required' : offer.trim().length > 300 ? 'Too long' : undefined
  const costTrim = cost.trim()
  const costError = costTrim !== '' && parseGBP(costTrim) == null ? 'Enter a £ amount or leave blank' : undefined
  const retailTrim = retailPrice.trim()
  const retailError = retailTrim !== '' && parseGBP(retailTrim) == null ? 'Enter a £ amount or leave blank' : undefined
  const isValid = !offerError && !costError && !retailError
  const saving = createMut.isPending || patchMut.isPending
  const serverError = (createMut.error ?? patchMut.error ?? deleteMut.error) as Error | null

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!isValid || saving) return
    const body: EventCateringBody = {
      offer: offer.trim(),
      supplier: supplier.trim() || null,
      costPence: costTrim === '' ? null : parseGBP(costTrim),
      retailPricePence: retailTrim === '' ? null : parseGBP(retailTrim),
      costNote: costNote.trim() || null,
      status,
      isUpsell,
      notes: notes.trim() || null,
      sortOrder: initial?.sortOrder ?? 0
    }
    if (isEditing && initial) patchMut.mutate({ id: initial.id, body }, { onSuccess: () => onClose() })
    else createMut.mutate(body, { onSuccess: () => onClose() })
  }

  function handleDelete() {
    if (!initial) return
    if (window.confirm(`Remove catering "${initial.offer}"?`)) deleteMut.mutate(initial.id, { onSuccess: () => onClose() })
  }

  return (
    <ModalShell
      title={isEditing ? 'Edit catering' : 'New catering'}
      onClose={onClose}
      onSubmit={handleSubmit}
      size="lg"
      footer={
        <>
          {isEditing && <DangerButton onClick={handleDelete} disabled={saving || deleteMut.isPending}>{deleteMut.isPending ? 'Removing…' : 'Remove'}</DangerButton>}
          <CancelButton onClick={onClose} disabled={saving} />
          <PrimaryButton disabled={!isValid || saving}>{saving ? 'Saving…' : isEditing ? 'Save changes' : 'Add catering'}</PrimaryButton>
        </>
      }
      body={
        <div className="space-y-4">
          <Field label="Offer" error={offerError}>
            <input className={inputClass(!!offerError)} value={offer} onChange={(e) => setOffer(e.target.value)} maxLength={300} autoFocus placeholder="e.g. Festive drink and sweet treat included in ticket price" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Supplier (optional)">
              <input className={inputClass(false)} value={supplier} onChange={(e) => setSupplier(e.target.value)} maxLength={160} placeholder="e.g. York Gin" />
            </Field>
            <Field label="Status">
              <select className={inputClass(false)} value={status} onChange={(e) => setStatus(e.target.value as CateringStatus)}>
                {CATERING_STATUSES.map((s) => <option key={s} value={s}>{STATUS_STYLE[s].label}</option>)}
              </select>
            </Field>
            {canFinancial && (
              <Field label="Cost (optional)" error={costError} hint="Supplier cost — £ amount">
                <input className={inputClass(!!costError)} value={cost} onChange={(e) => setCost(e.target.value)} placeholder="£0.00" />
              </Field>
            )}
            {canFinancial && (
              <Field label="Retail price (optional)" error={retailError} hint="What the customer pays — £ amount">
                <input className={inputClass(!!retailError)} value={retailPrice} onChange={(e) => setRetailPrice(e.target.value)} placeholder="£0.00" />
              </Field>
            )}
            <Field label="Cost note (optional)" hint="e.g. per head, included, TBC">
              <input className={inputClass(false)} value={costNote} onChange={(e) => setCostNote(e.target.value)} maxLength={160} placeholder="To be confirmed" />
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={isUpsell} onChange={(e) => setIsUpsell(e.target.checked)} />
            This is an upsell (sold as an add-on, not included in the ticket)
          </label>
          <Field label="Notes (optional)">
            <textarea className={inputClass(false)} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
          {serverError && <ServerError title="Could not save" error={serverError} />}
        </div>
      }
    />
  )
}
